<template>
  <v-layout align-start>
    <v-flex>

      <!-- Header -->
      <v-toolbar flat color="transparent" class="mb-2">
        <v-toolbar-title class="font-weight-bold">Donaciones</v-toolbar-title>
        <v-divider class="mx-3" inset vertical></v-divider>
        <v-select
          v-model="filterStatus"
          :items="statusOptions"
          label="Estado"
          clearable dense outlined hide-details
          style="max-width: 160px"
          class="mr-3"
        ></v-select>
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Buscar..."
          single-line hide-details dense outlined
          style="max-width: 220px"
        ></v-text-field>
      </v-toolbar>

      <!-- Stats -->
      <v-row class="px-3 pb-3" v-if="stats" dense>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 d-flex align-center">
            <v-avatar size="40" color="green darken-4" dark class="mr-3">
              <v-icon color="green darken-2">attach_money</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold" style="font-size:18px; color:#a5d6a7">${{ stats.totalAmount || 0 }}</div>
              <div class="grey--text" style="font-size:11px">Total recaudado</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 d-flex align-center">
            <v-avatar size="40" color="blue darken-4" dark class="mr-3">
              <v-icon color="blue darken-2">calendar_month</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold" style="font-size:18px; color:#90caf9">${{ stats.thisMonth || 0 }}</div>
              <div class="grey--text" style="font-size:11px">Este mes</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 d-flex align-center">
            <v-avatar size="40" color="rgba(128,203,196,0.16)" class="mr-3">
              <v-icon color="teal lighten-3">check_circle</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold" style="font-size:18px; color:#80cbc4">{{ stats.approved || 0 }}</div>
              <div class="grey--text" style="font-size:11px">Aprobadas</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 d-flex align-center">
            <v-avatar size="40" color="rgba(255,183,77,0.16)" class="mr-3">
              <v-icon color="orange lighten-3">hourglass_empty</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold" style="font-size:18px; color:#ffb74d">{{ stats.pending || 0 }}</div>
              <div class="grey--text" style="font-size:11px">Pendientes</div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="filteredDonations"
        :search="search"
        class="elevation-1"
        sort-by="createdAt"
        sort-desc
      >
        <!-- Donante -->
        <template v-slot:item.donorName="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="34" :color="statusAvatarColor(item.status)" class="mr-2">
              <span class="font-weight-bold" :style="{ color: statusAvatarText(item.status), fontSize: '12px' }">
                {{ initials(item.donorName) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium" style="font-size:13px">{{ item.donorName }}</div>
              <div class="grey--text text--darken-1" style="font-size:11px">{{ item.donorEmail }}</div>
            </div>
          </div>
        </template>

        <!-- Monto -->
        <template v-slot:item.amount="{ item }">
          <v-chip small color="green darken-4" dark style="color:#a5d6a7; font-weight:700; font-size:13px">
            ${{ item.amount }}
          </v-chip>
        </template>

        <!-- Estado -->
        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark>
            <v-icon x-small left>{{ statusIcon(item.status) }}</v-icon>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Mensaje -->
        <template v-slot:item.message="{ item }">
          <v-tooltip bottom v-if="item.message" max-width="400">
            <template v-slot:activator="{ on }">
              <v-chip v-on="on" x-small outlined color="blue-grey" style="max-width:160px; cursor:pointer">
                <span class="text-truncate" style="max-width:140px">{{ item.message }}</span>
              </v-chip>
            </template>
            <span>{{ item.message }}</span>
          </v-tooltip>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Fecha -->
        <template v-slot:item.createdAt="{ item }">
          <span class="text--secondary" style="font-size:12px">{{ formatDate(item.createdAt) }}</span>
        </template>

        <!-- Acciones -->
        <template v-slot:item.opciones="{ item }">
          <div class="d-flex align-center">
            <v-btn x-small text color="primary" class="mr-1 px-1" @click="verDetalle(item)">
              <v-icon x-small left>visibility</v-icon>Ver
            </v-btn>
            <template v-if="item.status === 'PENDING'">
              <v-btn x-small color="success" dark class="mr-1 px-1" @click="changeStatus(item, 'APPROVED')">
                <v-icon x-small left>check</v-icon>Aprobar
              </v-btn>
              <v-btn x-small color="error" outlined class="px-1" @click="changeStatus(item, 'REJECTED')">
                <v-icon x-small left>close</v-icon>Rechazar
              </v-btn>
            </template>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center pa-8 grey--text">
            <v-icon large color="grey darken-1">payments</v-icon>
            <div class="mt-2">No hay donaciones</div>
          </div>
        </template>
      </v-data-table>

      <!-- Modal de Detalle -->
      <v-dialog v-model="detalleModal" max-width="560">
        <v-card v-if="selectedDonation">
          <div :style="detailHeaderStyle(selectedDonation.status)" style="padding: 20px 24px;">
            <div class="d-flex align-center">
              <v-avatar size="44" color="white" class="mr-3">
                <span style="font-weight:800; font-size:15px" :style="{ color: detailAvatarTextColor(selectedDonation.status) }">
                  {{ initials(selectedDonation.donorName) }}
                </span>
              </v-avatar>
              <div>
                <div class="white--text font-weight-bold" style="font-size:16px">{{ selectedDonation.donorName }}</div>
                <div class="white--text" style="font-size:13px; opacity:.85">{{ selectedDonation.donorEmail }}</div>
              </div>
              <v-spacer></v-spacer>
              <v-chip dark outlined style="border-color:rgba(255,255,255,.6); color:#fff; font-weight:700; font-size:16px">
                ${{ selectedDonation.amount }}
              </v-chip>
            </div>
          </div>

          <v-card-text class="pt-4 pb-2">
            <v-row dense>
              <v-col cols="6">
                <div class="caption grey--text">Estado</div>
                <v-chip small :color="statusColor(selectedDonation.status)" dark class="mt-1">
                  <v-icon x-small left>{{ statusIcon(selectedDonation.status) }}</v-icon>
                  {{ statusLabel(selectedDonation.status) }}
                </v-chip>
              </v-col>
              <v-col cols="6">
                <div class="caption grey--text">Fecha</div>
                <div style="font-size:13px">{{ formatDate(selectedDonation.createdAt) }}</div>
              </v-col>
              <v-col cols="6" v-if="selectedDonation.donorPhone">
                <div class="caption grey--text">Teléfono</div>
                <div style="font-size:13px">{{ selectedDonation.donorPhone }}</div>
              </v-col>
            </v-row>

            <v-card
              :color="selectedDonation.message ? 'rgba(100,181,246,0.14)' : 'rgba(255,255,255,0.05)'"
              flat class="pa-3 mt-3"
            >
              <div class="caption grey--text mb-1">
                <v-icon small class="mr-1">message</v-icon>Mensaje del donante
              </div>
              <div v-if="selectedDonation.message" class="text--secondary" style="font-size:14px; line-height:1.6; white-space:pre-wrap">
                "{{ selectedDonation.message }}"
              </div>
              <div v-else class="grey--text font-italic" style="font-size:13px">Sin mensaje.</div>
            </v-card>

            <div v-if="selectedDonation.mpPaymentId || selectedDonation.mpPreferenceId" class="mt-3">
              <v-divider class="mb-2"></v-divider>
              <div class="caption grey--text mb-2">Información de pago</div>
              <div v-if="selectedDonation.mpPaymentId" style="font-size:12px"><strong>Payment ID:</strong> {{ selectedDonation.mpPaymentId }}</div>
              <div v-if="selectedDonation.mpPreferenceId" style="font-size:12px"><strong>Preference ID:</strong> {{ selectedDonation.mpPreferenceId }}</div>
              <div v-if="selectedDonation.mpStatus" style="font-size:12px"><strong>Estado MP:</strong> {{ selectedDonation.mpStatus }}</div>
            </div>
          </v-card-text>

          <v-card-actions class="pb-4 px-4">
            <template v-if="selectedDonation.status === 'PENDING'">
              <v-btn color="success" dark elevation="0" small @click="changeStatus(selectedDonation, 'APPROVED'); detalleModal = false">
                <v-icon left small>check_circle</v-icon>Aprobar
              </v-btn>
              <v-btn color="error" outlined small @click="changeStatus(selectedDonation, 'REJECTED'); detalleModal = false">
                <v-icon left small>cancel</v-icon>Rechazar
              </v-btn>
            </template>
            <v-spacer></v-spacer>
            <v-btn text @click="detalleModal = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  data() {
    return {
      search: "",
      filterStatus: null,
      detalleModal: false,
      selectedDonation: null,
      statusOptions: [
        { text: "Pendientes", value: "PENDING" },
        { text: "Aprobadas", value: "APPROVED" },
        { text: "Rechazadas", value: "REJECTED" },
        { text: "Reembolsadas", value: "REFUNDED" },
      ],
      headers: [
        { text: "Acciones", value: "opciones", sortable: false, width: "180px" },
        { text: "Donante", value: "donorName", sortable: true },
        { text: "Monto", value: "amount", sortable: true },
        { text: "Estado", value: "status", sortable: true },
        { text: "Mensaje", value: "message", sortable: false },
        { text: "Fecha", value: "createdAt", sortable: true },
      ],
    };
  },
  created() {
    this.getDonations();
    this.getStats();
  },
  computed: {
    ...mapState("donationNamespace", ["donations", "stats"]),
    filteredDonations() {
      const list = Array.isArray(this.donations) ? this.donations : [];
      if (!this.filterStatus) return list;
      return list.filter(d => d.status === this.filterStatus);
    },
  },
  methods: {
    ...mapActions("donationNamespace", ["getDonations", "getStats", "updateStatus"]),
    verDetalle(item) {
      this.selectedDonation = item;
      this.detalleModal = true;
    },
    changeStatus(item, status) {
      this.updateStatus({ id: item._id, status });
    },
    initials(name) {
      if (!name) return "?";
      return name.trim().split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
    },
    statusColor(status) {
      return { APPROVED: "green darken-1", PENDING: "orange darken-1", REJECTED: "red darken-1", REFUNDED: "blue-grey" }[status] || "grey";
    },
    statusIcon(status) {
      return { APPROVED: "check_circle", PENDING: "hourglass_empty", REJECTED: "cancel", REFUNDED: "undo" }[status] || "help";
    },
    statusLabel(status) {
      return { APPROVED: "Aprobada", PENDING: "Pendiente", REJECTED: "Rechazada", REFUNDED: "Reembolsada" }[status] || status;
    },
    statusAvatarColor(status) {
      return { APPROVED: "green darken-2", PENDING: "orange darken-3", REJECTED: "red darken-2", REFUNDED: "blue-grey darken-2" }[status] || "grey darken-2";
    },
    statusAvatarText(status) {
      return { APPROVED: "#2e7d32", PENDING: "#e65100", REJECTED: "#c62828", REFUNDED: "#546e7a" }[status] || "#555";
    },
    detailHeaderStyle(status) {
      const gradients = {
        APPROVED: "linear-gradient(135deg,#2e7d32,#43a047)",
        PENDING: "linear-gradient(135deg,#e65100,#fb8c00)",
        REJECTED: "linear-gradient(135deg,#c62828,#e53935)",
        REFUNDED: "linear-gradient(135deg,#455a64,#607d8b)",
      };
      return { background: gradients[status] || "linear-gradient(135deg,#455a64,#607d8b)" };
    },
    detailAvatarTextColor(status) {
      return { APPROVED: "#2e7d32", PENDING: "#e65100", REJECTED: "#c62828", REFUNDED: "#546e7a" }[status] || "#555";
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      const d = new Date(dateString);
      return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
    },
  },
};
</script>
